const longDateFormatter = new Intl.DateTimeFormat("es-AR", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

function normalizedDate(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return `${match[1]}-${match[2]}-${match[3]}`;
}

function formatLongDate(value) {
  const normalized = normalizedDate(value);
  if (!normalized) return null;
  const [year, month, day] = normalized.split("-").map(Number);
  return longDateFormatter.format(new Date(year, month - 1, day, 12));
}

function uniqueText(values = []) {
  return [...new Set(values.map((value) => String(value || "").trim()).filter(Boolean))];
}

function turnEvents(expertProceedings = []) {
  return expertProceedings.flatMap((expert, expertIndex) =>
    (expert.events || [])
      .filter((event) =>
        event.kind === "turno_estudios" &&
        Boolean(
          event.appointmentDate ||
          event.appointmentTime ||
          event.place ||
          event.detail ||
          event.studies?.length,
        ))
      .map((event, eventIndex) => ({
        expert,
        event,
        key: [
          expert.expertKey || expert.id || expertIndex,
          normalizedDate(event.appointmentDate) || event.occurredAt || eventIndex,
          ...(event.sourceIds || []),
        ].join(":"),
      })),
  );
}

function expertProceedingsFromFacts(procedureFacts = []) {
  const experts = procedureFacts.flatMap((fact) => fact?.data?.experts || []);
  return experts.map((expert, index) => {
    const turn = expert?.checklist?.turno_estudios;
    if (!turn) return { expertKey: expert?.key || `fact-expert-${index}`, events: [] };
    return {
      expertKey: expert?.key || `fact-expert-${index}`,
      name: expert?.name || null,
      events: [{
        kind: "turno_estudios",
        status: turn.status || null,
        occurredAt: turn.occurredAt || null,
        sourceIds: (turn.evidence || [])
          .map((evidence) => evidence?.sourceId || evidence?.actId)
          .filter(Boolean),
        detail: turn.detail || null,
        appointmentDate: turn.data?.appointmentDate || null,
        appointmentTime: turn.data?.appointmentTime || null,
        place: turn.data?.place || null,
        studies: turn.data?.studies || [],
      }],
    };
  });
}

function noticeMessage({ expediente, actorName, event }) {
  const caseNumber = expediente?.numero || null;
  const caption = String(expediente?.caratula || "").trim();
  const date = formatLongDate(event.appointmentDate);
  const time = String(event.appointmentTime || "").trim();
  const place = String(event.place || "").trim();
  const studies = uniqueText(event.studies);
  const heading = caseNumber
    ? `*Expediente N.º ${caseNumber}*`
    : `*Expediente ${expediente?.cuij || ""}*`;
  const lines = [
    "Hola, te comparto una información importante:",
    "",
    heading,
    caption ? `*Carátula:* ${caption}` : null,
    "",
    "🩺 *Turno para estudios médicos*",
    actorName ? `👤 *Debe concurrir:* ${actorName}` : null,
    date ? `📅 *Fecha:* ${date}` : null,
    time ? `🕐 *Hora:* ${time.replace(/\s*(hs?\.?|horas?)$/i, "")} hs.` : null,
    place ? `📍 *Lugar:* ${place}` : null,
    studies.length ? "" : null,
    studies.length ? "*Estudios indicados:*" : null,
    ...studies.map((study) => `• ${study}`),
    !date && !time && !place && event.detail ? `*Detalle:* ${event.detail}` : null,
    "",
    "Por favor, confirmá la recepción de este mensaje. Si tenés algún inconveniente para asistir, avisanos cuanto antes.",
  ];
  return lines.filter((line) => line !== null).join("\n").trim();
}

export function buildExpedienteWhatsappTurnNotices({
  expediente,
  expertProceedings = [],
  procedureFacts = [],
  actorName = null,
  today = null,
} = {}) {
  const todayDate = normalizedDate(today);
  const notices = turnEvents([
    ...expertProceedings,
    ...expertProceedingsFromFacts(procedureFacts),
  ])
    .filter(({ event }) => {
      const appointmentDate = normalizedDate(event.appointmentDate);
      return !todayDate || !appointmentDate || appointmentDate >= todayDate;
    })
    .map(({ expert, event, key }) => ({
      id: `whatsapp-turn:${key}`,
      kind: "medical_studies_appointment",
      label: "Aviso de turno para estudios médicos",
      appointmentDate: normalizedDate(event.appointmentDate),
      appointmentTime: event.appointmentTime || null,
      place: event.place || null,
      studies: uniqueText(event.studies),
      expertName: expert.name || null,
      sourceIds: uniqueText(event.sourceIds),
      message: noticeMessage({ expediente, actorName, event }),
    }));

  const bySignature = new Map();
  for (const notice of notices) {
    const signature = [
      notice.appointmentDate,
      notice.appointmentTime,
      notice.place,
      ...notice.studies,
    ].filter(Boolean).join(":").toLowerCase() || notice.id;
    const previous = bySignature.get(signature);
    bySignature.set(signature, previous
      ? {
          ...previous,
          sourceIds: uniqueText([
            ...previous.sourceIds,
            ...notice.sourceIds,
          ]),
        }
      : notice);
  }
  const byId = new Map(
    [...bySignature.values()].map((notice) => [notice.id, notice]),
  );
  return [...byId.values()].sort((left, right) =>
    String(left.appointmentDate || "9999-12-31").localeCompare(
      String(right.appointmentDate || "9999-12-31"),
    ),
  );
}

export function whatsappNoticeForInconsistency(issue, notices = []) {
  const issueSources = new Set(issue?.sourceIds || []);
  const matching = notices.find((notice) =>
    notice.sourceIds.some((sourceId) => issueSources.has(sourceId)),
  );
  return matching || (notices.length === 1 ? notices[0] : null);
}
